import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Cell,
    Column,
    Row,
    Table,
    TableBody,
    TableHeader
} from 'react-aria-components';
import toast from 'react-hot-toast';
import { RefreshIcon } from '../../shared/icons';
import apiClient from '../../shared/api/apiClient';
import './styles.css';

const AttributeDataType = {
    Text: 1,
    Number: 2,
    Boolean: 3,
    Date: 4,
    List: 5
} as const;

type AttributeDataType = typeof AttributeDataType[keyof typeof AttributeDataType];

interface AttributeDefinitionDto {
    id: number;
    code: string;
    name: string;
    dataType: AttributeDataType;
    dataTypeName: string;
    groupId?: number;
    groupName?: string;
    unitId?: number;
    unitName?: string;
    unitSymbol?: string;
    isMultivalue: boolean;
    isFilterable: boolean;
    isSearchable: boolean;
    sortOrder: number;
    description?: string;
}

interface AttributeGroupDto {
    id: number;
    name: string;
}

interface AttributeUnitDto {
    id: number;
    name: string;
    symbol?: string;
}

interface AttributeCatalogResponse {
    attributes: AttributeDefinitionDto[];
}

interface AttributeGroupsResponse {
    groups: AttributeGroupDto[];
}

interface AttributeUnitsResponse {
    units: AttributeUnitDto[];
}

interface AttributeFormState {
    id?: number;
    code: string;
    name: string;
    dataType: AttributeDataType;
    groupId: string;
    unitId: string;
    isMultivalue: boolean;
    isFilterable: boolean;
    isSearchable: boolean;
    sortOrder: string;
    description: string;
}

const emptyForm: AttributeFormState = {
    code: '',
    name: '',
    dataType: AttributeDataType.Text,
    groupId: '',
    unitId: '',
    isMultivalue: false,
    isFilterable: false,
    isSearchable: false,
    sortOrder: '0',
    description: ''
};

const dataTypeLabels: Record<AttributeDataType, string> = {
    [AttributeDataType.Text]: 'Текст',
    [AttributeDataType.Number]: 'Число',
    [AttributeDataType.Boolean]: 'Да/нет',
    [AttributeDataType.Date]: 'Дата',
    [AttributeDataType.List]: 'Список'
};

const attributePageSizeOptions = [10, 20, 50, 100];

const toForm = (attribute: AttributeDefinitionDto): AttributeFormState => ({
    id: attribute.id,
    code: attribute.code,
    name: attribute.name,
    dataType: attribute.dataType,
    groupId: attribute.groupId?.toString() ?? '',
    unitId: attribute.unitId?.toString() ?? '',
    isMultivalue: attribute.isMultivalue,
    isFilterable: attribute.isFilterable,
    isSearchable: attribute.isSearchable,
    sortOrder: attribute.sortOrder.toString(),
    description: attribute.description ?? ''
});

const AttributeManagement: React.FC = () => {
    const [attributes, setAttributes] = useState<AttributeDefinitionDto[]>([]);
    const [groups, setGroups] = useState<AttributeGroupDto[]>([]);
    const [units, setUnits] = useState<AttributeUnitDto[]>([]);
    const [form, setForm] = useState<AttributeFormState>(emptyForm);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem('attributeManagement_pageSize');
        return saved ? Number(saved) || 20 : 20;
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadCatalog = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const [attributesResponse, groupsResponse, unitsResponse] = await Promise.all([
                apiClient.get<AttributeCatalogResponse>('api/attributes'),
                apiClient.get<AttributeGroupsResponse>('api/attributes/groups'),
                apiClient.get<AttributeUnitsResponse>('api/attributes/units')
            ]);

            setAttributes(attributesResponse.data.attributes ?? []);
            setGroups(groupsResponse.data.groups ?? []);
            setUnits(unitsResponse.data.units ?? []);
        } catch (loadError) {
            console.error('Error loading attributes:', loadError);
            setError('Не удалось загрузить справочник атрибутов');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCatalog();
    }, [loadCatalog]);

    useEffect(() => {
        localStorage.setItem('attributeManagement_pageSize', pageSize.toString());
    }, [pageSize]);

    const filteredAttributes = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) {
            return attributes;
        }

        return attributes.filter(attribute =>
            attribute.name.toLowerCase().includes(term)
            || attribute.code.toLowerCase().includes(term)
            || attribute.groupName?.toLowerCase().includes(term)
            || attribute.unitName?.toLowerCase().includes(term));
    }, [attributes, searchTerm]);

    const selectedAttribute = useMemo(
        () => attributes.find(attribute => attribute.id === form.id),
        [attributes, form.id]);

    const totalPages = Math.max(1, Math.ceil(filteredAttributes.length / pageSize));
    const currentSafePage = Math.min(currentPage, totalPages);

    const paginatedAttributes = useMemo(() => {
        const startIndex = (currentSafePage - 1) * pageSize;
        return filteredAttributes.slice(startIndex, startIndex + pageSize);
    }, [currentSafePage, filteredAttributes, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, pageSize]);

    const handleCreate = () => {
        setForm(emptyForm);
    };

    const handleEdit = (attribute: AttributeDefinitionDto) => {
        setForm(toForm(attribute));
    };

    const handleRowAction = (key: React.Key) => {
        const attributeId = Number(key);
        const attribute = attributes.find(item => item.id === attributeId);

        if (attribute) {
            handleEdit(attribute);
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setSaving(true);

        const payload = {
            code: form.code,
            name: form.name,
            dataType: Number(form.dataType),
            groupId: form.groupId ? Number(form.groupId) : null,
            unitId: form.unitId ? Number(form.unitId) : null,
            isMultivalue: form.isMultivalue,
            isFilterable: form.isFilterable,
            isSearchable: form.isSearchable,
            sortOrder: form.sortOrder ? Number(form.sortOrder) : 0,
            description: form.description
        };

        try {
            const response = form.id
                ? await apiClient.put<AttributeDefinitionDto>(`api/attributes/${form.id}`, payload)
                : await apiClient.post<AttributeDefinitionDto>('api/attributes', payload);

            setAttributes(previous => {
                const saved = response.data;
                const withoutSaved = previous.filter(attribute => attribute.id !== saved.id);
                return [...withoutSaved, saved].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
            });
            setForm(toForm(response.data));
            toast.success('Атрибут сохранен');
        } catch (saveError) {
            console.error('Error saving attribute:', saveError);
        } finally {
            setSaving(false);
        }
    };

    if (error) {
        return (
            <div className="attribute-management container-fluid">
                <div className="alert alert-danger">
                    <h5 className="alert-heading">Ошибка</h5>
                    <p>{error}</p>
                    <button className="btn btn-primary" onClick={loadCatalog}>
                        Повторить загрузку
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="attribute-management container-fluid">
            <div className="filter-controls-compact attribute-toolbar">
                <div className="filter-controls-row">
                    <div>
                        <h3>Атрибуты НЛ</h3>
                        <p className="attribute-subtitle">Единый справочник характеристик для фильтров и B2B</p>
                    </div>

                    <div className="filter-controls-right">
                        <div className="search-input-compact">
                            <span className="search-icon">⌕</span>
                            <input
                                type="text"
                                className="form-control-compact"
                                placeholder="Поиск по названию, коду, группе"
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                            />
                            {searchTerm && (
                                <button className="clear-search" onClick={() => setSearchTerm('')} title="Очистить поиск">
                                    ×
                                </button>
                            )}
                        </div>

                        <button className="btn-compact refresh" onClick={loadCatalog} disabled={loading} title="Обновить">
                            {loading ? <span className="btn-spinner"></span> : <RefreshIcon />}
                        </button>
                        <button className="btn btn-primary" onClick={handleCreate}>
                            + Атрибут
                        </button>
                    </div>
                </div>
            </div>

            <div className="attribute-workspace">
                <div className="attribute-table-panel">
                    <div className="attribute-table-scroll">
                        <Table aria-label="Атрибуты НЛ" className="attribute-table" onRowAction={handleRowAction}>
                            <TableHeader>
                                <Column isRowHeader>Название</Column>
                                <Column>Код</Column>
                                <Column>Тип</Column>
                                <Column>Группа</Column>
                                <Column>Ед.</Column>
                                <Column>Фильтр</Column>
                                <Column>Поиск</Column>
                            </TableHeader>
                            <TableBody>
                                {paginatedAttributes.map(attribute => (
                                    <Row
                                        key={attribute.id}
                                        id={attribute.id}
                                        className={selectedAttribute?.id === attribute.id ? 'selected' : ''}
                                        textValue={`${attribute.name} ${attribute.code}`}
                                    >
                                        <Cell>
                                            <div className="attribute-name-cell">
                                                <strong>{attribute.name}</strong>
                                                {attribute.description && <span>{attribute.description}</span>}
                                            </div>
                                        </Cell>
                                        <Cell><code>{attribute.code}</code></Cell>
                                        <Cell>{dataTypeLabels[attribute.dataType] ?? attribute.dataTypeName}</Cell>
                                        <Cell>{attribute.groupName ?? '-'}</Cell>
                                        <Cell>{attribute.unitSymbol || attribute.unitName || '-'}</Cell>
                                        <Cell>
                                            <input
                                                aria-label="Фильтруемый"
                                                checked={attribute.isFilterable}
                                                className="attribute-grid-checkbox"
                                                readOnly
                                                type="checkbox"
                                            />
                                        </Cell>
                                        <Cell>
                                            <input
                                                aria-label="Поисковый"
                                                checked={attribute.isSearchable}
                                                className="attribute-grid-checkbox"
                                                readOnly
                                                type="checkbox"
                                            />
                                        </Cell>
                                    </Row>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    {!loading && filteredAttributes.length === 0 && (
                        <div className="attribute-empty-state">
                            Атрибуты не найдены
                        </div>
                    )}

                    {filteredAttributes.length > 0 && (
                        <div className="attribute-pagination">
                            <div className="pagination-info">
                                Всего: {filteredAttributes.length} | Страница {currentSafePage} из {totalPages}
                            </div>

                            <div className="page-size-selector">
                                <label htmlFor="attributePageSize">Размер страницы:</label>
                                <select
                                    id="attributePageSize"
                                    value={pageSize}
                                    onChange={(event) => setPageSize(Number(event.target.value))}
                                >
                                    {attributePageSizeOptions.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="pagination-buttons">
                                <button
                                    disabled={currentSafePage <= 1}
                                    onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                                    type="button"
                                >
                                    ‹‹
                                </button>

                                {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                                    const startPage = Math.max(1, Math.min(currentSafePage - 2, totalPages - 4));
                                    const page = startPage + index;

                                    return (
                                        <button
                                            className={page === currentSafePage ? 'active' : ''}
                                            disabled={page === currentSafePage}
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            type="button"
                                        >
                                            {page}
                                        </button>
                                    );
                                })}

                                <button
                                    disabled={currentSafePage >= totalPages}
                                    onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                                    type="button"
                                >
                                    ››
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <form className="attribute-editor" onSubmit={handleSubmit}>
                    <div className="attribute-editor-header">
                        <h4>{form.id ? 'Редактирование атрибута' : 'Новый атрибут'}</h4>
                        <span>{form.id ? `ID ${form.id}` : 'Черновик'}</span>
                    </div>

                    <label>
                        Название
                        <input
                            className="attribute-input"
                            value={form.name}
                            onChange={(event) => setForm(current => ({ ...current, name: event.target.value }))}
                            required
                        />
                    </label>

                    <label>
                        Код
                        <input
                            className="attribute-input"
                            value={form.code}
                            onChange={(event) => setForm(current => ({ ...current, code: event.target.value }))}
                            required
                        />
                    </label>

                    <div className="attribute-form-grid">
                        <label>
                            Тип
                            <select
                                className="attribute-input"
                                value={form.dataType}
                                onChange={(event) => setForm(current => ({ ...current, dataType: Number(event.target.value) as AttributeDataType }))}
                            >
                                {Object.entries(dataTypeLabels).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </label>

                        <label>
                            Порядок
                            <input
                                type="number"
                                className="attribute-input"
                                value={form.sortOrder}
                                onChange={(event) => setForm(current => ({ ...current, sortOrder: event.target.value }))}
                            />
                        </label>
                    </div>

                    <label>
                        Группа
                        <select
                            className="attribute-input"
                            value={form.groupId}
                            onChange={(event) => setForm(current => ({ ...current, groupId: event.target.value }))}
                        >
                            <option value="">Без группы</option>
                            {groups.map(group => (
                                <option key={group.id} value={group.id}>{group.name}</option>
                            ))}
                        </select>
                    </label>

                    <label>
                        Единица измерения
                        <select
                            className="attribute-input"
                            value={form.unitId}
                            onChange={(event) => setForm(current => ({ ...current, unitId: event.target.value }))}
                        >
                            <option value="">Без единицы</option>
                            {units.map(unit => (
                                <option key={unit.id} value={unit.id}>{unit.name}{unit.symbol ? ` (${unit.symbol})` : ''}</option>
                            ))}
                        </select>
                    </label>

                    <label>
                        Описание
                        <textarea
                            className="attribute-input"
                            value={form.description}
                            rows={3}
                            onChange={(event) => setForm(current => ({ ...current, description: event.target.value }))}
                        />
                    </label>

                    <div className="attribute-checkboxes">
                        <label><input type="checkbox" checked={form.isFilterable} onChange={(event) => setForm(current => ({ ...current, isFilterable: event.target.checked }))} /> Фильтруемый</label>
                        <label><input type="checkbox" checked={form.isSearchable} onChange={(event) => setForm(current => ({ ...current, isSearchable: event.target.checked }))} /> Поисковый</label>
                        <label><input type="checkbox" checked={form.isMultivalue} onChange={(event) => setForm(current => ({ ...current, isMultivalue: event.target.checked }))} /> Несколько значений</label>
                    </div>

                    <button className="btn btn-primary attribute-save-button" type="submit" disabled={saving}>
                        {saving ? 'Сохранение...' : 'Сохранить'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AttributeManagement;
